import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, FileText, Building2, Calendar, 
  MessageSquare, HelpCircle, LogOut, Menu, X, ChevronRight,
  Wallet, TrendingUp, TrendingDown, ChevronDown, Briefcase
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c422f3aa-3782-49d4-8e3c-f15c34e2c74c/artifacts/halfbrs0_wizbang.png";

const sidebarLinks = [
  { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'Team', path: '/admin/team', icon: Users },
  { name: 'Blogs', path: '/admin/blogs', icon: FileText },
  { name: 'Case Studies', path: '/admin/case-studies', icon: Briefcase },
  { name: 'Clients', path: '/admin/clients', icon: Building2 },
  { name: 'Stats', path: '/admin/stats', icon: LayoutDashboard },
  { name: 'Careers', path: '/admin/careers', icon: Users },
  { name: 'Appointments', path: '/admin/appointments', icon: Calendar },
  { name: 'Contacts', path: '/admin/contacts', icon: MessageSquare },
  { name: 'FAQ', path: '/admin/faq', icon: HelpCircle },
];

const erpLinks = [
  { name: 'ERP Dashboard', path: '/admin/erp', icon: Wallet },
  { name: 'ERP Clients', path: '/admin/erp/clients', icon: Users },
  { name: 'Income', path: '/admin/erp/income', icon: TrendingUp },
  { name: 'Expenses', path: '/admin/erp/expenses', icon: TrendingDown },
];

export default function AdminLayout({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [erpExpanded, setErpExpanded] = useState(location.pathname.startsWith('/admin/erp'));

  useEffect(() => {
    const token = localStorage.getItem('wizbang_token');
    const userData = localStorage.getItem('wizbang_user');
    
    if (!token) {
      navigate('/admin');
      return;
    }
    
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('wizbang_token');
    localStorage.removeItem('wizbang_user');
    navigate('/admin');
  };

  return (
    <div className="dark-section min-h-screen flex" data-testid="admin-layout" style={{ background: '#050505' }}>
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] border-r border-[#1F1F1F] transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#1F1F1F]">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Wizbang" className="h-8" />
              <span className="text-[#555] text-xs font-mono">ADMIN</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {sidebarLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`admin-nav-${link.name.toLowerCase()}`}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#ff5a1f]/10 text-[#ff5a1f] border-l-2 border-[#ff5a1f]'
                      : 'text-[#888] hover:text-white hover:bg-white/5'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.name}
                </Link>
              );
            })}

            {/* ERP Section Divider */}
            <div className="pt-4 pb-2">
              <div className="border-t border-[#1F1F1F]" />
            </div>

            {/* ERP Dropdown */}
            <button
              onClick={() => setErpExpanded(!erpExpanded)}
              data-testid="admin-nav-erp-toggle"
              className={`flex items-center justify-between w-full px-4 py-3 text-sm font-medium transition-colors ${
                location.pathname.startsWith('/admin/erp')
                  ? 'text-[#ff5a1f]'
                  : 'text-[#888] hover:text-white hover:bg-white/5'
              }`}
            >
              <div className="flex items-center gap-3">
                <Wallet className="w-5 h-5" />
                <span>ERP System</span>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform ${erpExpanded ? 'rotate-180' : ''}`} />
            </button>

            {/* ERP Sub-links */}
            {erpExpanded && (
              <div className="ml-4 space-y-1 border-l border-[#1F1F1F]">
                {erpLinks.map((link) => {
                  const isActive = location.pathname === link.path;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`admin-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                      className={`flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'text-[#ff5a1f] bg-[#ff5a1f]/10'
                          : 'text-[#666] hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <link.icon className="w-4 h-4" />
                      {link.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-[#1F1F1F]">
            {user && (
              <div className="px-4 py-2 mb-2">
                <p className="text-white text-sm font-medium truncate">{user.name}</p>
                <p className="text-[#555] text-xs truncate">{user.email}</p>
              </div>
            )}
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-3 px-4 py-3 text-sm text-[#888] hover:text-red-500 w-full transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-xl border-b border-[#1F1F1F]">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden text-white p-2"
                data-testid="admin-menu-btn"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              
              <div className="flex items-center gap-2 text-[#555] text-sm">
                <Link to="/admin/dashboard" className="hover:text-white transition-colors">
                  Admin
                </Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-white">{title}</span>
              </div>
            </div>

            <Link
              to="/"
              className="text-[#888] text-sm hover:text-[#ff5a1f] transition-colors"
            >
              View Site
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
